/**
 * Custom tooltip for the Site KPI OTIF chart - matches DefaultTab implementation.
 * Shows count breakdown (Passed, Failed, Arrears, Total) then percentages.
 *
 * CR-5868: moved out of SiteKPITab.tsx unchanged when the tab was split into
 * panels to stay under the 500-line limit.
 *
 * CR-6155: percentages follow the company's KPI decimal setting (was a fixed 2).
 */
import { usePreferences } from '../../../contexts/PreferencesContext'
import { formatChartPercent } from '../chartHeadline'

export type ChartTooltipItem = {
  dataKey?: string
  name?: string
  value?: number | string | null
  color?: string
  payload?: Record<string, number | string | null | undefined>
}

export function OTIFChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  const { preferences } = usePreferences()
  const decimals = preferences.kpi_percent_decimals ?? 0
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const toNum = (val: number | string | null | undefined): number => {
    if (val == null || val === '') return 0
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isFinite(n) ? n : 0
  }

  const row = payload[0]?.payload
  const otifCount = toNum(byKey['otifCount']?.value)
  const failed = toNum(byKey['failed']?.value)
  const arrears = toNum(byKey['arrears']?.value)
  const total = toNum(row?.['totalLines']) || (otifCount + failed + arrears)
  const otifFromBackend = row?.['otif']
  const otif = otifFromBackend != null && otifFromBackend !== '' ? toNum(otifFromBackend) : null
  const target = toNum(byKey['target']?.value)
  const trend = byKey['otifTrend']?.value

  type Row = { label: string; value: string; color?: string }
  const rows: Row[] = [
    { label: 'Passed OTIF PO Lines', value: String(Math.round(otifCount)), color: byKey['otifCount']?.color },
    { label: 'Failed OTIF PO Lines', value: String(Math.round(failed)), color: byKey['failed']?.color },
    { label: 'Arrears PO Lines', value: String(Math.round(arrears)), color: byKey['arrears']?.color },
    { label: 'Total PO Lines', value: String(Math.round(total)), color: '#6b7280' },
    { label: 'OTIF %', value: otif !== null ? formatChartPercent(otif, decimals) : '-', color: byKey['otif']?.color },
    { label: 'Target %', value: formatChartPercent(target, decimals), color: byKey['target']?.color },
  ]
  if (trend != null && trend !== '') {
    rows.push({ label: 'Trend %', value: formatChartPercent(toNum(trend), decimals), color: byKey['otifTrend']?.color })
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{ margin: 0, padding: 10, backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
    >
      {label && <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>}
      <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {rows.map((row) => (
          <li key={row.label} className="recharts-tooltip-item" style={{ display: 'block', paddingTop: 4, paddingBottom: 4, color: row.color }}>
            <span className="recharts-tooltip-item-name">{row.label}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
